import React from "react";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { GroupAdminInfo } from "../../api/groups";
import { Partner } from "../../api/partners";
import { usePartnersStore } from "../../store/partnersStore";
import { useDialogState } from "../../hooks/useDialogState";
import { GroupDetailDialog } from "../groups/GroupDetailDialog";
import { InfinitePaginationTable } from "../common/InfinitePaginationTable";
import { PartnerDetailDialog } from "./PartnerDetailDialog";
import { PartnerGroupsPanel } from "./PartnerGroupsPanel";
import { PartnerMembersListDialog } from "./PartnerMembersListDialog";

export const PartnerList = () => {
  const { partners, loading, loadPartners } = usePartnersStore();
  const detailDialog = useDialogState<Partner>();
  const membersDialog = useDialogState<Partner>();
  const groupDialog = useDialogState<GroupAdminInfo>();

  React.useEffect(() => {
    loadPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      id: "displayName",
      label: "Partner",
      render: (partner: Partner) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            src={partner.logoUrl}
            alt={partner.displayName}
            sx={{ width: 28, height: 28, bgcolor: partner.primaryColor }}
          >
            {partner.displayName.charAt(0).toUpperCase()}
          </Avatar>
          <Typography variant="body2">{partner.displayName}</Typography>
        </Box>
      ),
    },
    {
      id: "hashtag",
      label: "Tag",
      hideOnMobile: true,
      render: (partner: Partner) => (
        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
          #{partner.hashtag}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      render: (partner: Partner) => {
        const isActive = partner.status === "Active";
        return (
          <Chip
            label={partner.status}
            size="small"
            sx={{
              backgroundColor: isActive ? "success.light" : "action.disabledBackground",
              color: isActive ? "success.dark" : "text.secondary",
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      id: "actions",
      label: "Actions",
      hideOnMobile: true,
      render: (partner: Partner) => (
        <Box sx={{ display: "flex", flexWrap: "nowrap" }}>
          <Tooltip title="View details">
            <IconButton
              size="small"
              onClick={() => detailDialog.openWith(partner)}
              sx={{ color: "#1976d2" }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View members">
            <IconButton
              size="small"
              onClick={() => membersDialog.openWith(partner)}
              sx={{ color: "#1976d2" }}
            >
              <PeopleOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const sortedPartners = [...partners].sort((a, b) =>
    a.displayName.localeCompare(b.displayName)
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <InfinitePaginationTable
        columns={columns}
        items={sortedPartners}
        pageSize={10}
        loading={loading}
        onLoadNext={() => {}}
        renderExpandedRow={(partner: Partner) => (
          <PartnerGroupsPanel
            partner={partner}
            onViewGroup={groupDialog.openWith}
          />
        )}
      />
      <PartnerDetailDialog
        open={detailDialog.open}
        partner={detailDialog.data}
        onClose={detailDialog.close}
      />
      {membersDialog.data && (
        <PartnerMembersListDialog
          open={membersDialog.open}
          partner={membersDialog.data}
          onClose={membersDialog.close}
        />
      )}
      <GroupDetailDialog
        open={groupDialog.open}
        group={groupDialog.data}
        onClose={groupDialog.close}
      />
    </Box>
  );
};
